import React, { useState, useEffect } from "react";
import {
  X,
  Plus,
  Trash2,
  Edit2,
  Check,
  XCircle,
  MessageSquare,
  Save,
} from "lucide-react";
import apiService from "../services/api";
import "../styles/ExtrasModal.css";

// Category options for extras and deductions
const EXTRA_CATEGORIES = [
  { value: "דלק", label: "דלק" },
  { value: "נסיעות", label: "נסיעות" },
  { value: "בונוס", label: "בונוס" },
  { value: "כללי", label: "כללי" },
];

const DEDUCTION_CATEGORIES = [
  { value: "מקדמה", label: "מקדמה" },
  { value: "כללי", label: "כללי" },
  { value: "שכר דירה", label: "שכר דירה" },
  { value: "הפרשות", label: "הפרשות" },
  { value: "הלוואה", label: "הלוואה" },
];

const ExtrasModal = ({ isOpen, onClose, salary, onUpdate, currentMonth }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newItem, setNewItem] = useState({
    type: "extra",
    amount: "",
    category: "כללי",
    is_recurring: false,
  });
  const [editForm, setEditForm] = useState({});
  const [showNotesSection, setShowNotesSection] = useState(false);
  const [loanForm, setLoanForm] = useState({
    total_amount: "",
    num_months: "",
  });
  const [employeeLoans, setEmployeeLoans] = useState([]);
  const [loansLoading, setLoansLoading] = useState(false);

  // Determine if this is a company salary based on employee_type
  const isCompanySalary =
    salary?.employee_type?.includes("חברה") ||
    salary?.employee_type === "קבלן" ||
    salary?.employee_type === "קבלן ו חברה";

  // Get categories based on type
  const getCategoriesForType = (type) => {
    return type === "extra" ? EXTRA_CATEGORIES : DEDUCTION_CATEGORIES;
  };

  useEffect(() => {
    if (isOpen && salary) {
      loadExtras();
      loadEmployeeLoans();
    }
  }, [isOpen, salary]);

  const loadEmployeeLoans = async () => {
    try {
      setLoansLoading(true);
      const response = await apiService.getLoans({
        employee_id: salary.employee_id,
        status: "active",
      });
      if (response.success) {
        setEmployeeLoans(response.data || []);
      }
    } catch (error) {
      console.error("Failed to load loans:", error);
    } finally {
      setLoansLoading(false);
    }
  };

  // Parse employee notes from salary data (same as Presence page)
  const getEmployeeNotes = () => {
    if (!salary?.employee_notes) return [];
    return salary.employee_notes
      .split("|")
      .map((n) => n.trim())
      .filter(Boolean);
  };

  const loadExtras = async () => {
    try {
      setLoading(true);
      // All salaries from Salaries page are company salaries
      const response = await apiService.getCompanySalaryExtras(salary.id);
      if (response.success) {
        setItems(response.extras_details?.items || []);
      }
    } catch (error) {
      console.error("Failed to load extras:", error);
    } finally {
      setLoading(false);
    }
  };

  const isLoanCategory =
    newItem.type === "deduction" && newItem.category === "הלוואה";

  const handleAddItem = async () => {
    // If it's a loan, use the loan creation flow
    if (isLoanCategory) {
      await handleCreateLoan();
      return;
    }

    if (!newItem.amount || !newItem.category) {
      alert("נא למלא סכום וקטגוריה");
      return;
    }

    try {
      // Send category as comment to backend
      const itemToSend = {
        ...newItem,
        comment: newItem.category,
      };
      // All salaries from Salaries page are company salaries
      const response = await apiService.addCompanySalaryExtra(
        salary.id,
        itemToSend,
      );
      if (response.success) {
        setItems([...items, response.item]);
        setNewItem({
          type: "extra",
          amount: "",
          category: "כללי",
          is_recurring: false,
        });
        onUpdate && onUpdate();
      }
    } catch (error) {
      console.error("Failed to add item:", error);
      alert("שגיאה בהוספת פריט");
    }
  };

  const handleCreateLoan = async () => {
    if (!loanForm.total_amount || !loanForm.num_months) {
      alert("נא למלא סכום הלוואה ומספר תשלומים");
      return;
    }

    const totalAmount = parseFloat(loanForm.total_amount);
    const numMonths = parseInt(loanForm.num_months);

    if (totalAmount <= 0 || numMonths <= 0) {
      alert("סכום ומספר תשלומים חייבים להיות חיוביים");
      return;
    }

    try {
      const startMonth = currentMonth || new Date().toISOString().slice(0, 7);
      const response = await apiService.createLoan({
        employee_id: salary.employee_id,
        total_amount: totalAmount,
        num_months: numMonths,
        start_month: startMonth,
        partnership_id: salary.partnership_id,
      });

      if (response.success) {
        const monthly = (totalAmount / numMonths).toFixed(2);
        alert(
          `הלוואה נוצרה בהצלחה!\nסכום כולל: ₪${totalAmount}\nתשלום חודשי: ₪${monthly}\nמספר תשלומים: ${numMonths}`,
        );
        setLoanForm({ total_amount: "", num_months: "" });
        setNewItem({
          type: "extra",
          amount: "",
          category: "כללי",
          is_recurring: false,
        });
        await loadEmployeeLoans();
        onUpdate && onUpdate();
      } else {
        alert(response.error || "שגיאה ביצירת הלוואה");
      }
    } catch (error) {
      console.error("Failed to create loan:", error);
      alert("שגיאה ביצירת הלוואה");
    }
  };

  const handleCancelLoan = async (loanId) => {
    if (!window.confirm("האם לבטל את ההלוואה?")) return;
    try {
      const response = await apiService.cancelLoan(loanId);
      if (response.success) {
        await loadEmployeeLoans();
        onUpdate && onUpdate();
      }
    } catch (error) {
      console.error("Failed to cancel loan:", error);
      alert("שגיאה בביטול הלוואה");
    }
  };

  const handleUpdateItem = async (itemId) => {
    try {
      // Send category as comment to backend
      const formToSend = {
        ...editForm,
        comment: editForm.category,
      };
      // All salaries from Salaries page are company salaries
      const response = await apiService.updateCompanySalaryExtra(
        salary.id,
        itemId,
        formToSend,
      );
      if (response.success) {
        await loadExtras();
        setEditingId(null);
        setEditForm({});
        onUpdate && onUpdate();
      }
    } catch (error) {
      console.error("Failed to update item:", error);
      alert("שגיאה בעדכון פריט");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("האם למחוק פריט זה?")) {
      return;
    }

    try {
      // All salaries from Salaries page are company salaries
      const response = await apiService.deleteCompanySalaryExtra(
        salary.id,
        itemId,
      );
      if (response.success) {
        setItems(items.filter((item) => item.id !== itemId));
        onUpdate && onUpdate();
      }
    } catch (error) {
      console.error("Failed to delete item:", error);
      alert("שגיאה במחיקת פריט");
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      type: item.type,
      amount: item.amount,
      category: item.comment,
      is_recurring: item.is_recurring,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const calculateTotals = () => {
    const extras = items
      .filter((item) => item.type === "extra")
      .reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

    const deductions = items
      .filter((item) => item.type === "deduction")
      .reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

    return { extras, deductions };
  };

  if (!isOpen) return null;

  const totals = calculateTotals();

  return (
    <div className='modal-overlay' onClick={onClose}>
      <div className='extras-modal' onClick={(e) => e.stopPropagation()}>
        <div className='modal-header'>
          <h2>תוספות וניכויים - {salary?.employee_name}</h2>
          <div className='header-actions'>
            <button
              className={`notes-toggle-btn ${showNotesSection ? "active" : ""}`}
              onClick={() => setShowNotesSection(!showNotesSection)}
              title='הערות'
            >
              <MessageSquare size={18} />
              הערות
            </button>
            <button className='close-btn' onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Notes Section */}
        {showNotesSection && (
          <div className='notes-section'>
            <h3>
              <MessageSquare size={18} /> הערות לעובד
            </h3>
            {getEmployeeNotes().length > 0 ? (
              <ul className='notes-list'>
                {getEmployeeNotes().map((note, index) => (
                  <li key={index}>{note}</li>
                ))}
              </ul>
            ) : (
              <p className='no-notes-message'>אין הערות לעובד זה</p>
            )}
          </div>
        )}

        <div className='modal-body'>
          {loading ? (
            <div className='loading'>טוען...</div>
          ) : (
            <>
              {/* Add New Item Form */}
              <div className='add-item-form'>
                <h3>הוסף פריט חדש</h3>
                <div className='form-row'>
                  <select
                    value={newItem.type}
                    onChange={(e) =>
                      setNewItem({
                        ...newItem,
                        type: e.target.value,
                        category: e.target.value === "extra" ? "כללי" : "כללי",
                      })
                    }
                  >
                    <option value='extra'>תוספת</option>
                    <option value='deduction'>ניכוי</option>
                  </select>

                  <select
                    value={newItem.category}
                    onChange={(e) =>
                      setNewItem({ ...newItem, category: e.target.value })
                    }
                    className='category-select'
                  >
                    {getCategoriesForType(newItem.type).map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>

                  {isLoanCategory ? (
                    <>
                      <input
                        type='number'
                        placeholder='סכום הלוואה כולל'
                        value={loanForm.total_amount}
                        onChange={(e) =>
                          setLoanForm({
                            ...loanForm,
                            total_amount: e.target.value,
                          })
                        }
                        step='0.01'
                        style={{ minWidth: "130px" }}
                      />
                      <input
                        type='number'
                        placeholder='מס׳ תשלומים'
                        value={loanForm.num_months}
                        onChange={(e) =>
                          setLoanForm({
                            ...loanForm,
                            num_months: e.target.value,
                          })
                        }
                        min='1'
                        style={{ minWidth: "100px" }}
                      />
                      {loanForm.total_amount &&
                        loanForm.num_months &&
                        parseInt(loanForm.num_months) > 0 && (
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#666",
                              whiteSpace: "nowrap",
                            }}
                          >
                            ₪
                            {(
                              parseFloat(loanForm.total_amount) /
                              parseInt(loanForm.num_months)
                            ).toFixed(2)}{" "}
                            / חודש
                          </span>
                        )}
                    </>
                  ) : (
                    <>
                      <input
                        type='number'
                        placeholder='סכום'
                        value={newItem.amount}
                        onChange={(e) =>
                          setNewItem({ ...newItem, amount: e.target.value })
                        }
                        step='0.01'
                      />

                      <label className='recurring-checkbox'>
                        <input
                          type='checkbox'
                          checked={newItem.is_recurring}
                          onChange={(e) =>
                            setNewItem({
                              ...newItem,
                              is_recurring: e.target.checked,
                            })
                          }
                        />
                        <span>חודשי</span>
                      </label>
                    </>
                  )}

                  <button className='add-btn' onClick={handleAddItem}>
                    <Plus size={18} />
                    {isLoanCategory ? "צור הלוואה" : "הוסף"}
                  </button>
                </div>
              </div>

              {/* Active Loans Section */}
              {employeeLoans.length > 0 && (
                <div className='items-list' style={{ marginBottom: "16px" }}>
                  <h3>הלוואות פעילות</h3>
                  <table className='items-table'>
                    <thead>
                      <tr>
                        <th>סכום כולל</th>
                        <th>תשלום חודשי</th>
                        <th>שולם</th>
                        <th>יתרה</th>
                        <th>פעולות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeLoans.map((loan) => (
                        <tr key={loan.id}>
                          <td>₪{loan.total_amount.toFixed(2)}</td>
                          <td>₪{loan.monthly_deduction.toFixed(2)}</td>
                          <td>
                            {loan.months_paid}/{loan.num_months}
                          </td>
                          <td>₪{loan.remaining_balance.toFixed(2)}</td>
                          <td className='actions-cell'>
                            <button
                              className='delete-btn'
                              onClick={() => handleCancelLoan(loan.id)}
                              title='בטל הלוואה'
                            >
                              <XCircle size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Items List */}
              <div className='items-list'>
                <h3>פריטים קיימים</h3>
                {items.length === 0 ? (
                  <p className='empty-message'>אין פריטים</p>
                ) : (
                  <table className='items-table'>
                    <thead>
                      <tr>
                        <th>סוג</th>
                        <th>סכום</th>
                        <th>קטגוריה</th>
                        <th>חודשי</th>
                        <th>פעולות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.id}>
                          {editingId === item.id ? (
                            <>
                              <td>
                                <select
                                  value={editForm.type}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      type: e.target.value,
                                      category: "כללי",
                                    })
                                  }
                                >
                                  <option value='extra'>תוספת</option>
                                  <option value='deduction'>ניכוי</option>
                                </select>
                              </td>
                              <td>
                                <input
                                  type='number'
                                  value={editForm.amount}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      amount: e.target.value,
                                    })
                                  }
                                  step='0.01'
                                />
                              </td>
                              <td>
                                <select
                                  value={editForm.category}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      category: e.target.value,
                                    })
                                  }
                                >
                                  {getCategoriesForType(editForm.type).map(
                                    (cat) => (
                                      <option key={cat.value} value={cat.value}>
                                        {cat.label}
                                      </option>
                                    ),
                                  )}
                                </select>
                              </td>
                              <td>
                                <input
                                  type='checkbox'
                                  checked={editForm.is_recurring}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      is_recurring: e.target.checked,
                                    })
                                  }
                                />
                              </td>
                              <td className='actions-cell'>
                                <button
                                  className='save-btn'
                                  onClick={() => handleUpdateItem(item.id)}
                                >
                                  <Check size={16} />
                                </button>
                                <button
                                  className='cancel-btn'
                                  onClick={cancelEdit}
                                >
                                  <XCircle size={16} />
                                </button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>
                                <span className={`type-badge ${item.type}`}>
                                  {item.type === "extra" ? "תוספת" : "ניכוי"}
                                </span>
                              </td>
                              <td className='amount-cell'>
                                ₪{parseFloat(item.amount).toFixed(2)}
                              </td>
                              <td>{item.comment}</td>
                              <td>{item.is_recurring ? "✓" : "-"}</td>
                              <td className='actions-cell'>
                                <button
                                  className='edit-btn'
                                  onClick={() => startEdit(item)}
                                >
                                  <Edit2 size={16} />
                                </button>
                                <button
                                  className='delete-btn'
                                  onClick={() => handleDeleteItem(item.id)}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Totals Summary */}
              <div className='totals-summary'>
                <div className='total-item extras'>
                  <span>סה״כ תוספות:</span>
                  <strong>₪{totals.extras.toFixed(2)}</strong>
                </div>
                <div className='total-item deductions'>
                  <span>סה״כ ניכויים:</span>
                  <strong>₪{totals.deductions.toFixed(2)}</strong>
                </div>
                <div className='total-item net'>
                  <span>נטו (תוספות - ניכויים):</span>
                  <strong>
                    ₪{(totals.extras - totals.deductions).toFixed(2)}
                  </strong>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExtrasModal;
